// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// View 1 -- Search. The default view, and the one real people actually use:
// looking up a grandparent, a club president, a name on a plaque.
//
// Three things drive the design.
//
// 1. The name index is SHARDED by initial and a shard is up to 3 MB, so nothing
//    is fetched on mount. The first query decides which letters to pull, they
//    are fetched in parallel, cached by data.ts for the rest of the session, and
//    a skeleton plus a progress line covers the wait.
// 2. A record is indexed under the initial of BOTH its surname and its given
//    name, so the same id can arrive twice. Dedupe by id before filtering.
// 3. The URL carries the query so a search is shareable. Live typing syncs it
//    with replaceState -- routing through ctx.go on every keystroke would
//    re-render the whole view and eat the caret -- and an explicit submit uses
//    ctx.go for a real history entry (a no-op when the hash already matches).

import { loadShard, toRecord } from '../data';
import { escapeHtml, fold, longDate, num, pct, tipAttr } from '../format';
import { gloss } from '../glossary';
import { RAMP, systemColour } from '../palette';
import type { Core, RawRecord, Record_, View } from '../types';

const PAGE = 300;
const LETTER_CAP = 5;
const REGISTER_URL = 'https://honours.pmc.gov.au/honours/search';

/** Folded names, cached per shard so repeat queries never re-fold 30k strings. */
const foldedCache = new Map<string, string[]>();
/** Letters already in memory, so the progress line only shows for a real fetch. */
const loadedLetters = new Set<string>();

/** fold(), plus curly apostrophes flattened on BOTH sides of the comparison. */
function normalise(s: string): string {
  return fold(s).replace(/[‘’ʼ`´]/g, "'");
}

function tokenise(q: string): string[] {
  const out: string[] = [];
  for (const raw of normalise(q).split(/[^a-z0-9'-]+/)) {
    const t = raw.replace(/^['-]+|['-]+$/g, '');
    if (t) out.push(t);
  }
  return out.slice(0, 8);
}

/** Award index -> award system, read off core.hierarchy (system -> award). */
function systemByAward(core: Core): string[] {
  const m = new Map<string, string>();
  for (const s of core.hierarchy) for (const c of s.children ?? []) m.set(c.name, s.name);
  return core.dicts.award.map((a) => m.get(a) ?? 'Unknown');
}

/**
 * The register gazettes surnames in capitals ("Mr John Adrian Louis HOPE"), so
 * the last all-caps word is the surname. Ranks are capitalised too, which is
 * why this takes the LAST one, not the first.
 */
function surnameKey(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i].replace(/[^A-Za-z'’-]/g, '');
    if (p.length >= 2 && p === p.toUpperCase() && /[A-Z]/.test(p)) return normalise(p);
  }
  return normalise(parts[parts.length - 1] ?? name);
}

function titleCasePlace(s: string): string {
  if (!s) return '';
  if (s !== s.toUpperCase()) return s;
  return s
    .toLowerCase()
    .replace(/(^|[\s'()/-])([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase());
}

export const searchView: View = (root, ctx) => {
  const { core, meta } = ctx;
  const awardSystem = systemByAward(core);
  const systemNames = new Set(core.systems.map((s) => s.name));

  let disposed = false;
  let seq = 0;
  let debounce: number | undefined;
  let focusTimer: number | undefined;

  // ---------------------------------------------------------------- state

  interface SearchState {
    q: string;
    system: string;
    decade: number | null;
    sort: 'name' | 'year';
  }

  const p0 = new URLSearchParams(root.dataset.params ?? '');
  const rawDecade = Number(p0.get('decade'));
  const state: SearchState = {
    q: p0.get('q') ?? '',
    system: systemNames.has(p0.get('system') ?? '') ? (p0.get('system') as string) : '',
    decade:
      Number.isFinite(rawDecade) && rawDecade >= 1800 && rawDecade <= 2100
        ? Math.floor(rawDecade / 10) * 10
        : null,
    sort: p0.get('sort') === 'year' ? 'year' : 'name',
  };

  let matched: RawRecord[] = []; // name matches, before the filters
  let shown: Record_[] = []; // exactly what is drawn, indexed by data-i
  let limit = PAGE;
  let loading = false;
  let loadingLabel = '';
  let failure = '';
  let cappedLetters = false;
  let hasDigitToken = false;

  const sortKeys = new Map<number, string>();
  const keyOf = (r: RawRecord): string => {
    let k = sortKeys.get(r[0]);
    if (k === undefined) {
      k = `${surnameKey(r[1])} ${normalise(r[1])}`;
      sortKeys.set(r[0], k);
    }
    return k;
  };

  // ------------------------------------------------------------------ url

  function urlParams(): Record<string, string> {
    const p: Record<string, string> = {};
    if (state.q.trim()) p.q = state.q.trim();
    if (state.system) p.system = state.system;
    if (state.decade !== null) p.decade = String(state.decade);
    if (state.sort !== 'name') p.sort = state.sort;
    return p;
  }

  function syncUrl(replace: boolean): void {
    const p = urlParams();
    const qs = new URLSearchParams(p).toString();
    const hash = `#/search${qs ? `?${qs}` : ''}`;
    if (location.hash === hash) return;
    if (replace) history.replaceState(null, '', hash);
    else ctx.go('search', p);
  }

  // ----------------------------------------------------------------- shell

  const placeCoverAll = core.totals.records > 0 ? core.totals.withPostcode / core.totals.records : 0;
  const placeCoverOa = core.totals.oa > 0 ? core.totals.oaWithPostcode / core.totals.oa : 0;

  root.innerHTML = `
    <div class="view-head">
      <h1>Search the honours register</h1>
      <p class="lede">All <strong>${num(core.totals.records)}</strong> Australian honours and awards
        on the public register, searchable by the name they were
        ${gloss('gazette', 'gazetted')} under. One row is one award, not one person.</p>
    </div>

    <form class="panel" id="s-form" role="search" aria-label="Search the honours register">
      <label for="s-q" style="display:block;font-weight:600;margin-bottom:var(--space-sm)">
        Recipient name</label>
      <div class="controls" style="margin-bottom:var(--space-md)">
        <input type="search" id="s-q" name="q" autocomplete="off" spellcheck="false"
          placeholder="Surname, given name, or both"
          value="${escapeHtml(state.q)}"
          style="flex:1 1 18rem;min-width:0;max-width:100%">
        <button type="submit" class="pill" style="min-height:44px;padding:0 1.1rem;font-weight:600">
          Search</button>
      </div>
      <div class="controls">
        <label for="s-sys" style="display:inline-flex;align-items:center;gap:.4rem;min-width:0;
          font-size:var(--font-size-sm);color:var(--text-secondary)">
          Award system
          <select id="s-sys" style="max-width:min(100%,24rem)">
            <option value="">All award systems</option>
            ${core.systems
              .map(
                (s) =>
                  `<option value="${escapeHtml(s.name)}"${state.system === s.name ? ' selected' : ''}>${escapeHtml(
                    s.name,
                  )} — ${num(s.n)} records</option>`,
              )
              .join('')}
          </select>
        </label>
        <div class="control-group" role="group" aria-label="Sort the results">
          <button type="button" data-sort="name" aria-pressed="false">By name</button>
          <button type="button" data-sort="year" aria-pressed="false">Newest first</button>
        </div>
        <button type="button" class="pill" id="s-clear" hidden>Clear filters</button>
      </div>
      <p class="panel-sub" style="margin-top:var(--space-md)">
        Register snapshot: awards up to <strong>${escapeHtml(longDate(meta.as_at_award_date))}</strong>.
        A suburb is published for ${pct(placeCoverAll)} of all records and ${pct(placeCoverOa)} of
        ${gloss('order', 'Order of Australia')} records, so most rows show no location.
      </p>
    </form>

    <div id="s-status" aria-live="polite" style="margin-bottom:var(--space-md)"></div>
    <section id="s-chart" class="panel" hidden></section>
    <section id="s-results"></section>
  `;

  const form = root.querySelector<HTMLFormElement>('#s-form')!;
  const input = root.querySelector<HTMLInputElement>('#s-q')!;
  const sysSel = root.querySelector<HTMLSelectElement>('#s-sys')!;
  const clearBtn = root.querySelector<HTMLButtonElement>('#s-clear')!;
  const statusEl = root.querySelector<HTMLElement>('#s-status')!;
  const chartEl = root.querySelector<HTMLElement>('#s-chart')!;
  const resultsEl = root.querySelector<HTMLElement>('#s-results')!;

  // ---------------------------------------------------------------- search

  function foldedFor(letter: string, rows: RawRecord[]): string[] {
    let f = foldedCache.get(letter);
    if (!f) {
      f = rows.map((r) => normalise(r[1]));
      foldedCache.set(letter, f);
    }
    return f;
  }

  async function runSearch(): Promise<void> {
    const my = ++seq;
    const tokens = tokenise(state.q);
    hasDigitToken = tokens.some((t) => /^\d+$/.test(t));
    matched = [];
    limit = PAGE;
    failure = '';
    cappedLetters = false;

    const letters = [
      ...new Set(
        tokens
          .map((t) => t[0])
          .filter((c) => c >= 'a' && c <= 'z')
          .map((c) => c.toUpperCase()),
      ),
    ];
    if (state.q.trim().length < 2 || tokens.length === 0 || letters.length === 0) {
      loading = false;
      paint();
      return;
    }

    const used = letters.slice(0, LETTER_CAP);
    cappedLetters = letters.length > used.length;
    const pending = used.filter((l) => !loadedLetters.has(l));
    loading = pending.length > 0;
    loadingLabel = pending.join(', ');
    if (loading) paint();

    let shards: RawRecord[][];
    try {
      shards = await Promise.all(
        used.map((l) =>
          loadShard(l).then((rows) => {
            loadedLetters.add(l);
            return rows;
          }),
        ),
      );
    } catch (err) {
      if (disposed || my !== seq) return;
      loading = false;
      failure = (err as Error).message;
      paint();
      return;
    }
    if (disposed || my !== seq) return;

    const seen = new Set<number>();
    const out: RawRecord[] = [];
    for (let s = 0; s < shards.length; s++) {
      const rows = shards[s];
      const folded = foldedFor(used[s], rows);
      for (let i = 0; i < rows.length; i++) {
        const id = rows[i][0];
        if (seen.has(id)) continue;
        const f = folded[i];
        let ok = true;
        for (const t of tokens) {
          if (!f.includes(t)) {
            ok = false;
            break;
          }
        }
        if (!ok) continue;
        seen.add(id);
        out.push(rows[i]);
      }
    }
    matched = out;
    loading = false;
    paint();
  }

  // ----------------------------------------------------------------- chart

  function decadeChart(rows: RawRecord[]): string {
    const counts = new Map<number, number>();
    for (const r of rows) {
      const y = Number.isFinite(r[3]) ? r[3] : 0;
      if (y < 1800) continue;
      const d = Math.floor(y / 10) * 10;
      counts.set(d, (counts.get(d) ?? 0) + 1);
    }
    const keys = [...counts.keys()].sort((a, b) => a - b);
    if (keys.length === 0) return '';

    const spans: number[] = [];
    for (let d = keys[0]; d <= keys[keys.length - 1]; d += 10) spans.push(d);
    const n = Math.max(1, spans.length);

    const ML = 48;
    const MR = 14;
    const MT = 16;
    const MB = 56;
    // The SVG is drawn at its viewBox size in CSS pixels and NEVER scaled up:
    // a 5-bar chart stretched across a 1500px panel would render 11px axis text
    // at 50px. Below its own width it overflows and .chart-wrap scrolls, which
    // is what keeps the labels legible at 375px.
    const W = Math.max(560, Math.min(920, ML + MR + n * 54));
    const H = 220;
    const plotW = Math.max(40, W - ML - MR);
    const plotH = H - MT - MB;
    const band = plotW / n;
    const barW = Math.max(4, Math.min(band * 0.72, 72));
    const max = Math.max(1, ...spans.map((d) => counts.get(d) ?? 0));
    const colour = state.system ? systemColour(state.system) : RAMP[4];

    const ticks = [0, Math.round(max / 2), max].filter((v, i, a) => a.indexOf(v) === i);
    const grid = ticks
      .map((t) => {
        const y = MT + plotH - (t / max) * plotH;
        return `<line x1="${ML}" y1="${y.toFixed(1)}" x2="${(ML + plotW).toFixed(1)}" y2="${y.toFixed(
          1,
        )}" stroke="var(--border-subtle)" stroke-width="1"></line>
        <text x="${ML - 8}" y="${(y + 4).toFixed(1)}" text-anchor="end" font-size="11">${num(t)}</text>`;
      })
      .join('');

    const bars = spans
      .map((d, i) => {
        const c = counts.get(d) ?? 0;
        const h = max > 0 ? (c / max) * plotH : 0;
        const x = ML + i * band + (band - barW) / 2;
        const y = MT + plotH - h;
        const on = state.decade === null || state.decade === d;
        const label = `${d}s`;
        const tip = `${label}\n${num(c)} matching record${c === 1 ? '' : 's'}\n${
          state.decade === d ? 'Selected — click to clear' : 'Click to keep only this decade'
        }`;
        const showLabel = n <= 12 || i % 2 === 0 || i === n - 1;
        return `<g role="button" tabindex="0" data-decade="${d}"
            aria-pressed="${state.decade === d ? 'true' : 'false'}"
            aria-label="${escapeHtml(label)}: ${num(c)} matching records"
            data-tip="${tipAttr(tip)}" style="cursor:pointer">
            <rect x="${(ML + i * band).toFixed(1)}" y="${MT}" width="${band.toFixed(1)}"
              height="${plotH}" fill="#000" fill-opacity="0" pointer-events="all"></rect>
            <rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(1)}"
              height="${Math.max(0, h).toFixed(1)}" fill="${colour}"
              opacity="${on ? 1 : 0.24}"></rect>
            ${
              showLabel
                ? `<text x="${(ML + i * band + band / 2).toFixed(1)}" y="${MT + plotH + 18}"
                    text-anchor="middle" font-size="11"
                    opacity="${on ? 1 : 0.5}">${escapeHtml(label)}</text>`
                : ''
            }
          </g>`;
      })
      .join('');

    return `<svg class="chart" viewBox="0 0 ${W} ${H}" role="group"
        aria-label="Matching records by decade of award. Click a decade to filter."
        style="width:${W}px;min-width:${W}px;max-width:${W}px;height:auto">
        ${grid}
        <line x1="${ML}" y1="${MT + plotH}" x2="${(ML + plotW).toFixed(1)}" y2="${MT + plotH}"
          stroke="var(--border-default)" stroke-width="1"></line>
        ${bars}
        <text x="${(ML + plotW / 2).toFixed(1)}" y="${H - 8}" text-anchor="middle"
          font-size="11">Decade the award was made</text>
        <text transform="rotate(-90 13 ${MT + plotH / 2})" x="13" y="${MT + plotH / 2}"
          text-anchor="middle" font-size="11">Records</text>
      </svg>`;
  }

  // ----------------------------------------------------------------- paint

  function matchesSystem(r: RawRecord): boolean {
    return !state.system || awardSystem[r[2]] === state.system;
  }
  function matchesDecade(r: RawRecord): boolean {
    return state.decade === null || Math.floor((r[3] || 0) / 10) * 10 === state.decade;
  }

  function skeleton(): string {
    return [92, 74, 84, 66, 88, 71, 80, 62]
      .map(
        (w) =>
          `<div class="skeleton" style="height:1.1rem;margin:.5rem 0;width:${w}%;max-width:100%"></div>`,
      )
      .join('');
  }

  function emptyState(): string {
    const years = core.byYear.map((y) => y.year).filter((y) => Number.isFinite(y));
    const span = years.length ? `${Math.min(...years)}–${Math.max(...years)}` : '—';
    const examples = ['Nguyen', "Mary O'Brien", 'Kelly'];
    return `
      <div class="stats">
        <div class="stat" data-tip="${tipAttr(
          `${num(core.totals.records)} award records on the public register`,
        )}">
          <div class="stat-value">${num(core.totals.records)}</div>
          <div class="stat-label">Award records, searchable by name</div>
        </div>
        <div class="stat" data-tip="${tipAttr(`${num(core.totals.awards)} distinct awards, in ${num(core.totals.systems)} award systems`)}">
          <div class="stat-value">${num(core.totals.awards)}</div>
          <div class="stat-label">Distinct awards, across ${num(core.totals.systems)} systems</div>
        </div>
        <div class="stat" data-tip="${tipAttr(`Award years present in the register: ${span}`)}">
          <div class="stat-value">${escapeHtml(span)}</div>
          <div class="stat-label">Years of awards covered</div>
        </div>
        <div class="stat" data-tip="${tipAttr(
          `${num(core.totals.citations)} records carry a published citation (${pct(
            core.totals.records > 0 ? core.totals.citations / core.totals.records : 0,
          )} of the register)`,
        )}">
          <div class="stat-value">${num(core.totals.citations)}</div>
          <div class="stat-label">Records with a published ${gloss('citation')}</div>
        </div>
      </div>

      <div class="panel">
        <div class="panel-head"><h2>Start with a name</h2></div>
        <p class="panel-sub">This searches the recipient name only — every part of it, so
          <em>kelly</em> finds both a given name and a surname. Try:</p>
        <div class="controls" style="margin:var(--space-md) 0">
          ${examples
            .map(
              (e) =>
                `<button type="button" class="pill" data-example="${escapeHtml(e)}"
                   style="min-height:40px">${escapeHtml(e)}</button>`,
            )
            .join('')}
        </div>
        <ul style="margin:0 0 0 1.1rem;color:var(--text-secondary);font-size:var(--font-size-sm)">
          <li>Names appear as the register gazetted them — usually a title or rank, given names,
            then the surname in capitals.</li>
          <li>Married names, nicknames and later spelling corrections are not cross-referenced,
            so try more than one spelling.</li>
          <li>Open a record to read its ${gloss('citation')}, its
            ${gloss('honours-list', 'honours list')} and its ${gloss('post-nominal')}.</li>
          <li>There is no page for an individual on this site, and no person URL to share.</li>
        </ul>
        <div class="note">
          Two in five records on the register are the ${gloss('national-medal', 'National Medal')},
          a long-service medal for 15 years of service. A name search will often return
          long-service and campaign medals alongside everything else: they measure time served,
          not a ranking of achievement.
        </div>
      </div>`;
  }

  function noMatchState(): string {
    return `
      <div class="panel">
        <div class="panel-head"><h2>No record matches “${escapeHtml(state.q.trim())}”</h2></div>
        <p class="panel-sub">That does not mean the person was never honoured. The register is a
          published index with real gaps:</p>
        <ul style="margin:var(--space-md) 0 var(--space-md) 1.1rem;color:var(--text-secondary);
          font-size:var(--font-size-sm)">
          <li>Names are published exactly as ${gloss('gazette', 'gazetted')} — an initial instead
            of a given name, a maiden name, a different transliteration, or a typo in the original
            notice will all defeat an exact match.</li>
          <li>Recipients may ask for their entry not to be published, so some awards are held back
            from the public register altogether.</li>
          <li>Some very early and some Defence records are incomplete in the source.</li>
          ${
            hasDigitToken
              ? `<li>Your search includes a number. Numbers are not part of a name — use the
                  decade filter or the year column instead.</li>`
              : ''
          }
        </ul>
        <p class="panel-sub">Try a surname on its own, or check the
          <a href="${REGISTER_URL}" target="_blank" rel="noopener">official register search</a>
          at the Department of the Prime Minister and Cabinet.</p>
      </div>`;
  }

  function rowsTable(rows: RawRecord[]): string {
    const page = rows.slice(0, limit);
    shown = page.map((r) => toRecord(r, core.dicts));
    const body = shown
      .map((rec, i) => {
        const place = [titleCasePlace(rec.suburb), rec.state].filter(Boolean).join(', ');
        const sys = awardSystem[page[i][2]] ?? 'Unknown';
        const tip = `${rec.name}\n${rec.award}${sys ? ` (${sys})` : ''}\n${
          rec.year || 'Year not published'
        }\n${
          [titleCasePlace(rec.suburb), rec.state, rec.postcode].filter(Boolean).join(' ') ||
          'Location not published'
        }\n${rec.list || 'Not part of a named honours list'}`;
        return `<tr data-row data-i="${i}" data-tip="${tipAttr(tip)}">
          <td><button type="button" data-open="${i}"
              aria-label="${escapeHtml(`Open the register entry for ${rec.name}, ${rec.award}, ${rec.year || 'year unknown'}`)}"
              style="font:inherit;background:none;border:0;padding:0;text-align:left;
                color:var(--accent-secondary);font-weight:600;text-decoration:underline;
                text-underline-offset:2px;cursor:pointer;min-height:2.2rem">${escapeHtml(rec.name)}</button>${
                  rec.postNominal
                    ? ` <span class="num" style="color:var(--text-tertiary)">${escapeHtml(rec.postNominal)}</span>`
                    : ''
                }</td>
          <td><span class="swatch" style="display:inline-block;vertical-align:-2px;
              background:${systemColour(sys)}"></span> ${escapeHtml(rec.award)}</td>
          <td class="n">${rec.year ? escapeHtml(String(rec.year)) : '—'}</td>
          <td>${place ? escapeHtml(place) : '<span style="color:var(--text-tertiary)">Not published</span>'}</td>
          <td>${rec.list ? escapeHtml(rec.list) : '<span style="color:var(--text-tertiary)">—</span>'}</td>
        </tr>`;
      })
      .join('');

    return `<div class="scroller">
        <table style="min-width:46rem">
          <thead><tr>
            <th scope="col">Recipient, as gazetted</th>
            <th scope="col">Award</th>
            <th scope="col" class="n">Year</th>
            <th scope="col">Location as gazetted</th>
            <th scope="col">Honours list</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>`;
  }

  function systemLegend(rows: RawRecord[]): string {
    const by = new Map<string, number>();
    for (const r of rows) {
      const s = awardSystem[r[2]] ?? 'Unknown';
      by.set(s, (by.get(s) ?? 0) + 1);
    }
    const items = [...by.entries()].sort((a, b) => b[1] - a[1]);
    if (items.length === 0) return '';
    return `<div class="legend" role="group" aria-label="Filter these results by award system">
      ${items
        .map(
          ([s, n]) =>
            `<button type="button" class="legend-item" data-sys="${escapeHtml(s)}"
               aria-pressed="${state.system === s ? 'true' : 'false'}"
               data-tip="${tipAttr(`${s}\n${num(n)} of these records\n${state.system === s ? 'Selected — click to clear' : 'Click to keep only this system'}`)}"
               style="font:inherit;font-size:var(--font-size-sm);background:none;border:0;
                 cursor:pointer;min-height:40px;color:${state.system === s ? 'var(--text-primary)' : 'var(--text-secondary)'};
                 font-weight:${state.system === s ? '700' : '400'};
                 opacity:${!state.system || state.system === s ? 1 : 0.45}">
               <span class="swatch" style="background:${systemColour(s)}"></span>
               ${escapeHtml(s)} <span class="num">${num(n)}</span></button>`,
        )
        .join('')}
    </div>
    <p class="panel-sub" style="margin-top:var(--space-sm)">Colour marks the award system.
      Click one to keep only its records; click it again to clear.</p>`;
  }

  function paint(): void {
    // controls reflect state (they can be changed from the chart or the legend)
    sysSel.value = state.system;
    for (const b of root.querySelectorAll<HTMLButtonElement>('[data-sort]')) {
      b.setAttribute('aria-pressed', b.dataset.sort === state.sort ? 'true' : 'false');
    }
    clearBtn.hidden = !state.system && state.decade === null;

    const q = state.q.trim();

    if (failure) {
      statusEl.innerHTML = '';
      chartEl.hidden = true;
      resultsEl.innerHTML = `<div class="error" role="alert">
        <p>The name index could not be loaded.</p>
        <p class="panel-sub">${escapeHtml(failure)}</p>
        <p style="margin-top:var(--space-md)">
          <button type="button" class="pill" id="s-retry" style="min-height:40px">Try again</button>
        </p></div>`;
      return;
    }

    if (loading) {
      statusEl.innerHTML = `<p class="panel-sub">Loading the name index${
        loadingLabel ? ` for ${escapeHtml(loadingLabel)}` : ''
      } — this happens once per visit, then searching is instant.</p>`;
      chartEl.hidden = true;
      resultsEl.innerHTML = `<div class="panel" aria-busy="true">${skeleton()}</div>`;
      return;
    }

    if (!q) {
      statusEl.innerHTML = '';
      chartEl.hidden = true;
      resultsEl.innerHTML = emptyState();
      return;
    }

    if (q.length < 2 || tokenise(q).filter((t) => t[0] >= 'a' && t[0] <= 'z').length === 0) {
      statusEl.innerHTML = '';
      chartEl.hidden = true;
      resultsEl.innerHTML = `<div class="panel"><p class="panel-sub">
        Type at least two letters of a name. The index is built from name initials, so a search
        needs at least one word that starts with a letter.</p></div>`;
      return;
    }

    if (matched.length === 0) {
      statusEl.innerHTML = `<p class="panel-sub">No records match
        “${escapeHtml(q)}”.</p>`;
      chartEl.hidden = true;
      resultsEl.innerHTML = noMatchState();
      return;
    }

    const forChart = matched.filter(matchesSystem);
    const forLegend = matched.filter(matchesDecade);
    const finalRows = forChart.filter(matchesDecade);

    const decorated = finalRows.map((r) => ({ r, k: keyOf(r) }));
    if (state.sort === 'year') {
      decorated.sort((a, b) => b.r[3] - a.r[3] || (a.k < b.k ? -1 : a.k > b.k ? 1 : 0));
    } else {
      decorated.sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : a.r[3] - b.r[3]));
    }
    const ordered = decorated.map((d) => d.r);

    const filtered = state.system !== '' || state.decade !== null;
    statusEl.innerHTML = `
      <p style="font-size:var(--font-size-lg)"><strong class="num">${num(ordered.length)}</strong>
        record${ordered.length === 1 ? '' : 's'} match${ordered.length === 1 ? 'es' : ''}
        “${escapeHtml(q)}”${
          filtered
            ? ` with the current filters, out of <span class="num">${num(matched.length)}</span> by name`
            : ''
        }.</p>
      <p class="panel-sub">One row is one award. A person with more than one honour appears once
        for each.${
          cappedLetters
            ? ' Only the first five name words were used to look up the index, so a very long query may miss records.'
            : ''
        }</p>`;

    // chart
    const svg = decadeChart(forChart);
    if (svg) {
      chartEl.hidden = false;
      const colour = state.system ? systemColour(state.system) : RAMP[4];
      chartEl.innerHTML = `
        <div class="panel-head"><h2>When these awards were made</h2></div>
        <p class="panel-sub">Every record matching “${escapeHtml(q)}”${
          state.system ? ` in the ${escapeHtml(state.system)} system` : ''
        }, counted by the decade of the award. Bar height is the number of records in that decade.
        Click a decade to keep only those records.</p>
        <div class="chart-wrap">${svg}</div>
        <div class="legend">
          <span class="legend-item"><span class="swatch" style="background:${colour}"></span>
            Matching records${state.decade !== null ? ' in the selected decade' : ''}</span>
          ${
            state.decade !== null
              ? `<span class="legend-item"><span class="swatch"
                   style="background:${colour};opacity:.24"></span> Other decades, filtered out</span>`
              : ''
          }
        </div>
        ${
          state.decade !== null
            ? `<div class="controls" style="margin-top:var(--space-md);margin-bottom:0">
                 <button type="button" class="pill" id="s-alldec" style="min-height:40px">
                   Showing ${state.decade}s only — show all years</button></div>`
            : ''
        }`;
    } else {
      chartEl.hidden = true;
      chartEl.innerHTML = '';
    }

    // results
    if (ordered.length === 0) {
      resultsEl.innerHTML = `<div class="panel">
        <div class="panel-head"><h2>No records left after filtering</h2></div>
        <p class="panel-sub">${num(matched.length)} record${matched.length === 1 ? '' : 's'} match
          “${escapeHtml(q)}” by name, but none of them
          ${state.system ? `are in the ${escapeHtml(state.system)} system` : ''}${
            state.system && state.decade !== null ? ' and ' : ''
          }${state.decade !== null ? `were awarded in the ${state.decade}s` : ''}.</p>
        <div class="controls" style="margin-top:var(--space-md);margin-bottom:0">
          <button type="button" class="pill" data-clear="1" style="min-height:40px">
            Clear the filters</button></div>
        ${systemLegend(forLegend)}
      </div>`;
      return;
    }

    const capped = ordered.length > limit;
    resultsEl.innerHTML = `<div class="panel">
      <div class="panel-head"><h2>Matching records</h2></div>
      <p class="panel-sub">Sorted ${
        state.sort === 'year' ? 'by year, newest first' : 'by surname as gazetted'
      }. Select a name to open its register entry — the award, the
      ${gloss('honours-list', 'honours list')} and the ${gloss('citation')} where one is published.</p>
      ${rowsTable(ordered)}
      <p class="panel-sub" style="margin-top:var(--space-md)">${
        capped
          ? `Showing the first <span class="num">${num(limit)}</span> of
             <span class="num">${num(ordered.length)}</span> matching records — refine the search,
             or load more.`
          : `Showing all <span class="num">${num(ordered.length)}</span> matching records.`
      }</p>
      ${
        capped
          ? `<div class="controls" style="margin-top:var(--space-sm);margin-bottom:0">
               <button type="button" class="pill" id="s-more" style="min-height:40px">
                 Show ${num(Math.min(PAGE, ordered.length - limit))} more</button></div>`
          : ''
      }
      ${systemLegend(forLegend)}
    </div>`;
  }

  // ---------------------------------------------------------------- events

  function setQuery(v: string, replace: boolean): void {
    state.q = v;
    state.decade = null;
    limit = PAGE;
    syncUrl(replace);
    void runSearch();
  }

  input.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      if (disposed) return;
      setQuery(input.value, true);
    }, 300);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    window.clearTimeout(debounce);
    // An explicit submit is a real navigation, so it goes through ctx.go; it is
    // a no-op when the debounced replaceState already wrote the same hash.
    setQuery(input.value, false);
  });

  sysSel.addEventListener('change', () => {
    state.system = systemNames.has(sysSel.value) ? sysSel.value : '';
    limit = PAGE;
    syncUrl(true);
    paint();
  });

  form.addEventListener('click', (e) => {
    const sortBtn = (e.target as Element).closest<HTMLButtonElement>('[data-sort]');
    if (sortBtn) {
      state.sort = sortBtn.dataset.sort === 'year' ? 'year' : 'name';
      syncUrl(true);
      paint();
      return;
    }
    if ((e.target as Element).closest('#s-clear')) {
      state.system = '';
      state.decade = null;
      limit = PAGE;
      syncUrl(true);
      paint();
    }
  });

  chartEl.addEventListener('click', (e) => {
    const t = e.target as Element;
    if (t.closest('#s-alldec')) {
      state.decade = null;
      limit = PAGE;
      syncUrl(true);
      paint();
      return;
    }
    const bar = t.closest<SVGGElement>('[data-decade]');
    if (!bar) return;
    const d = Number(bar.getAttribute('data-decade'));
    if (!Number.isFinite(d)) return;
    state.decade = state.decade === d ? null : d;
    limit = PAGE;
    syncUrl(true);
    paint();
  });

  chartEl.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    const bar = (e.target as Element).closest<SVGGElement>('[data-decade]');
    if (!bar) return;
    e.preventDefault();
    const d = Number(bar.getAttribute('data-decade'));
    if (!Number.isFinite(d)) return;
    state.decade = state.decade === d ? null : d;
    limit = PAGE;
    syncUrl(true);
    paint();
  });

  resultsEl.addEventListener('click', (e) => {
    const t = e.target as Element;

    const example = t.closest<HTMLButtonElement>('[data-example]');
    if (example) {
      input.value = example.dataset.example ?? '';
      setQuery(input.value, false);
      input.focus();
      return;
    }
    if (t.closest('#s-retry')) {
      failure = '';
      void runSearch();
      return;
    }
    if (t.closest('#s-more')) {
      limit += PAGE;
      paint();
      return;
    }
    if (t.closest('[data-clear]')) {
      state.system = '';
      state.decade = null;
      limit = PAGE;
      syncUrl(true);
      paint();
      return;
    }
    const legend = t.closest<HTMLButtonElement>('[data-sys]');
    if (legend) {
      const s = legend.dataset.sys ?? '';
      state.system = state.system === s ? '' : s;
      limit = PAGE;
      syncUrl(true);
      paint();
      return;
    }
    // The name in each row is a real <button>, so Enter and Space already work
    // through this same handler; the row itself is the mouse target.
    const row = t.closest<HTMLTableRowElement>('tr[data-i]');
    if (row) {
      const i = Number(row.dataset.i);
      const rec = Number.isFinite(i) ? shown[i] : undefined;
      if (rec) ctx.openRecord(rec);
    }
  });

  // ------------------------------------------------------------------ boot

  // A query restored from the URL must paint as LOADING, never as "no match":
  // the shard fetch has not happened yet, so an empty result set is not a fact.
  if (state.q.trim().length >= 2) {
    loading = true;
    paint();
    void runSearch();
  } else {
    paint();
  }

  // Autofocus on desktop only: on a phone it throws up the keyboard and hides
  // everything that explains what this is. The timeout lets the shell finish
  // its own focus() call first.
  const desktop =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    window.innerWidth >= 900;
  if (desktop) {
    focusTimer = window.setTimeout(() => {
      if (disposed) return;
      input.focus({ preventScroll: true });
      try {
        input.setSelectionRange(input.value.length, input.value.length);
      } catch {
        /* selection is not supported on every engine for type=search */
      }
    }, 0);
  }

  return () => {
    disposed = true;
    window.clearTimeout(debounce);
    window.clearTimeout(focusTimer);
  };
};
